import { Banner } from "@/services/bannerService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Pencil, Trash2, Eye, Calendar, Target, FileX, ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, CircleSlash, Monitor, Newspaper, Maximize2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface BannerTableProps {
  banners: Banner[];
  onDelete: (bannerId: string) => void;
}

type BannerStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

const getBannerStatus = (banner: Banner): BannerStatus => {
  const now = new Date();
  const startDate = new Date(banner.startDate);
  const endDate = new Date(banner.endDate);

  if (!banner.active) return 'inactive';
  if (now < startDate) return 'scheduled';
  if (now > endDate) return 'expired';
  return 'active';
};

const formatDate = (date: string | Date) => {
  try {
    const d = new Date(date);
    return d.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Geçersiz Tarih';
  }
};

export function BannerTable({ banners, onDelete }: BannerTableProps) {
  const navigate = useNavigate();

  // Sayfalama için state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(banners.length / itemsPerPage);

  // Mevcut sayfada gösterilecek bannerları belirle
  const currentBanners = banners.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Sayfa değiştirme fonksiyonu
  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  // Önceki ve sonraki sayfalara geçiş fonksiyonları
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getDisplayTypeBadge = (displayType: string) => {
    switch (displayType) {
      case 'banner':
        return (
          <div className="inline-flex items-center rounded-lg bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-800">
            <Monitor className="h-3 w-3 mr-1.5 text-purple-600" />
            Banner
          </div>
        );
      case 'news':
        return (
          <div className="inline-flex items-center rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
            <Newspaper className="h-3 w-3 mr-1.5 text-blue-600" />
            Haber
          </div>
        );
      case 'modal':
        return (
          <div className="inline-flex items-center rounded-lg bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
            <Maximize2 className="h-3 w-3 mr-1.5 text-orange-600" />
            Modal
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
            <Monitor className="h-3 w-3 mr-1.5 text-gray-600" />
            Banner
          </div>
        );
    }
  };

  const getStatusBadge = (status: BannerStatus) => {
    switch (status) {
      case 'active':
        return (
          <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
            <CheckCircle className="h-3 w-3 mr-1.5 text-green-600" />
            Aktif
          </div>
        );
      case 'scheduled':
        return (
          <div className="inline-flex items-center rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
            <Clock className="h-3 w-3 mr-1.5 text-blue-600" />
            Planlanmış
          </div>
        );
      case 'expired':
        return (
          <div className="inline-flex items-center rounded-lg bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
            <XCircle className="h-3 w-3 mr-1.5 text-red-600" />
            Süresi Dolmuş
          </div>
        );
      case 'inactive':
        return (
          <div className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
            <CircleSlash className="h-3 w-3 mr-1.5 text-gray-600" />
            Pasif
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Banner Tablosu */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
              <TableHead className="py-4 pl-6 text-slate-700 font-bold text-sm w-[280px]">Duyuru Bilgileri</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">Tip</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">Durum</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Tarih Aralığı</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[120px]">Hedefleme</TableHead>
              <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentBanners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <FileX className="h-10 w-10 mb-2 text-slate-400" />
                    <p>Duyuru bulunamadı.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentBanners.map((banner) => {
                const status = getBannerStatus(banner);
                return (
                  <TableRow
                    key={banner._id}
                    className="border-b border-slate-100 hover:bg-purple-50/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/notices/${banner._id}`)}
                  >
                    <TableCell className="py-4 pl-6">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 break-words text-base">
                          {banner.title}
                        </span>
                        <span
                          className="text-sm text-slate-500 mt-0.5 truncate max-w-[230px]"
                          dangerouslySetInnerHTML={{
                            __html: banner.content.replace(/<[^>]*>/g, '').substring(0, 50) + '...'
                          }}
                        />
                        {banner.buttonText && (
                          <span className="text-xs text-purple-600 mt-1">
                            <Eye className="h-3 w-3 inline mr-1" />
                            CTA: {banner.buttonText}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      {getDisplayTypeBadge(banner.displayType || 'banner')}
                    </TableCell>
                    <TableCell className="py-4">
                      {getStatusBadge(status)}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center text-xs text-slate-600">
                          <Calendar className="h-3.5 w-3.5 mr-1.5 text-green-500" />
                          <span>{formatDate(banner.startDate)}</span>
                        </div>
                        <div className="flex items-center text-xs text-slate-600">
                          <Calendar className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                          <span>{formatDate(banner.endDate)}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      {banner.targetCompanies && banner.targetCompanies.length > 0 ? (
                        <div className="inline-flex items-center rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                          <Target className="h-3 w-3 mr-1.5 text-amber-600" />
                          {banner.targetCompanies.length} Firma
                        </div>
                      ) : (
                        <div className="inline-flex items-center rounded-lg bg-cyan-100 px-2.5 py-1 text-xs font-semibold text-cyan-800">
                          <Target className="h-3 w-3 mr-1.5 text-cyan-600" />
                          Tüm Firmalar
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right pr-6" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                            <span className="sr-only">İşlemler</span>
                            <MoreHorizontal className="h-5 w-5 text-slate-600" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/notices/${banner._id}`)}>
                            <Pencil className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Düzenle</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(banner.buttonLink || '#', '_blank')}>
                            <Eye className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Önizle</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => banner._id && onDelete(banner._id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            <span>Sil</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama kontrolleri */}
      {banners.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{banners.length}</span> duyuru içinden{" "}
            <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
            <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, banners.length)}</span>{" "}
            arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Önceki sayfa"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Önceki</span>
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Sayfa düğmeleri için başlangıç sayfasını hesapla
                let startPage = Math.max(1, currentPage - 2);
                if (currentPage > totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                }

                const page = startPage + i;
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => changePage(page)}
                      className={`h-10 min-w-10 px-3 rounded-lg ${
                        currentPage === page
                          ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Sonraki sayfa"
            >
              <span className="hidden sm:inline">Sonraki</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
